import React, { useState, useEffect } from 'react';
import { Form, Input, Button, DatePicker, InputNumber, Card, Row, Col, Space } from 'antd';
import { PlusOutlined, EditOutlined, CloseOutlined } from '@ant-design/icons';
import { validateDebtForm } from '../../../../shared/utils/validators';
import dayjs from 'dayjs';
import styles from './DebtForm.module.css';

const { TextArea } = Input;

export default function DebtForm({
    onSubmit,
    editingDebt,
    onCancelEdit,
    loading = false
}) {
    const [form] = Form.useForm();
    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        if (editingDebt) {
            form.setFieldsValue({
                ...editingDebt,
                ngayVay: dayjs(editingDebt.ngayVay),
                ngayTra: dayjs(editingDebt.ngayTra)
            });
        } else {
            form.resetFields();
        }
    }, [editingDebt, form]);

    const handleSubmit = async (values) => {
        const validation = validateDebtForm(values);

        if (!validation.isValid) {
            setFormErrors(validation.errors);
            return;
        }

        setFormErrors({});

        const debtData = {
            ...values,
            ngayVay: values.ngayVay.toDate(),
            ngayTra: values.ngayTra.toDate()
        };

        try {
            await onSubmit(debtData);
            if (!editingDebt) {
                form.resetFields();
            }
        } catch (error) {
            console.error('Error submitting form:', error);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setFormErrors({});
        onCancelEdit?.();
    };

    return (
        <Card
            title={
                <div className={styles.cardTitle}>
                    {editingDebt ? (
                        <>
                            <EditOutlined /> Chỉnh sửa nợ
                        </>
                    ) : (
                        <>
                            <PlusOutlined /> Thêm nợ mới
                        </>
                    )}
                </div>
            }
            className={styles.formCard}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{
                    daTra: false
                }}
            >
                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="ngayVay"
                            label="Ngày vay"
                            rules={[{ required: true, message: 'Vui lòng chọn ngày vay!' }]}
                            validateStatus={formErrors.ngayVay ? 'error' : ''}
                            help={formErrors.ngayVay}
                        >
                            <DatePicker
                                style={{ width: '100%' }}
                                format="DD/MM/YYYY"
                                placeholder="Chọn ngày vay"
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="ngayTra"
                            label="Ngày trả"
                            rules={[{ required: true, message: 'Vui lòng chọn ngày trả!' }]}
                            validateStatus={formErrors.ngayTra ? 'error' : ''}
                            help={formErrors.ngayTra}
                        >
                            <DatePicker
                                style={{ width: '100%' }}
                                format="DD/MM/YYYY"
                                placeholder="Chọn ngày trả"
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="nguoiVay"
                            label="Vay ai?"
                            rules={[{ required: true, message: 'Vui lòng nhập tên người cho vay!' }]}
                            validateStatus={formErrors.nguoiVay ? 'error' : ''}
                            help={formErrors.nguoiVay}
                        >
                            <Input placeholder="Nhập tên người cho vay" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="soTien"
                            label="Số tiền (VNĐ)"
                            rules={[{ required: true, message: 'Vui lòng nhập số tiền!' }]}
                            validateStatus={formErrors.soTien ? 'error' : ''}
                            help={formErrors.soTien}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                placeholder="Nhập số tiền"
                                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                min={0}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    name="mucDich"
                    label="Mục đích"
                    rules={[{ required: true, message: 'Vui lòng nhập mục đích vay!' }]}
                    validateStatus={formErrors.mucDich ? 'error' : ''}
                    help={formErrors.mucDich}
                >
                    <TextArea
                        rows={3}
                        placeholder="Nhập mục đích vay tiền"
                        maxLength={500}
                        showCount
                    />
                </Form.Item>

                <Form.Item>
                    <Space>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            icon={editingDebt ? <EditOutlined /> : <PlusOutlined />}
                        >
                            {editingDebt ? 'Cập nhật' : 'Thêm nợ'}
                        </Button>

                        {editingDebt && (
                            <Button
                                onClick={handleCancel}
                                icon={<CloseOutlined />}
                            >
                                Hủy
                            </Button>
                        )}
                    </Space>
                </Form.Item>
            </Form>
        </Card>
    );
}
